import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useConfiguratorStore from '../store/configuratorStore';
import SubcategoryNavigation from './SubcategoryNavigation';
import ProductList from './ProductList';
import AttributeConfigurator from './AttributeConfigurator';
import QuantitySelector from './QuantitySelector';
import DeckConfigurator from './DeckConfigurator';
import UnloadingStationConfigurator from './UnloadingStationConfigurator';
import MeetNevaSection from './MeetNevaSection';
import { getProductImageSrc } from '../utils/imageUtils';
import LazyImage from './shared/LazyImage';

const ConfiguratorContainer = styled(motion.div)`
  width: ${props => props.$isFullWidth ? '100%' : '60%'};
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding-top: 64px; /* Space for the category navigation */
  border-radius: 0;
  box-shadow: none;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
  }
`;

const WelcomeMessage = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--gray-600);
`;

const WelcomeIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--gray-400);
`;

const WelcomeTitle = styled.h2`
  font-size: var(--text-xl);
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--gray-700);
`;

const WelcomeText = styled.p`
  font-size: var(--text-md);
  max-width: 500px;
  margin: 0 auto;
`;

const ConfiguratorContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem 1.5rem;
  scroll-behavior: smooth; /* Ensure smooth scrolling */

  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: var(--gray-400) var(--gray-100);

  /* For Chrome, Edge and Safari */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--gray-100);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--gray-400);
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    padding: 0 1rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.75rem 0.75rem;
  }
`;

const ConfiguratorSection = styled.div`
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--gray-800);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--rounded-full);
  background-color: var(--primary-color);
  color: white;
  font-size: var(--text-sm);
  font-weight: 600;
`;

const ProductSummaryCard = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

const ProductHeader = styled.div`
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.875rem;
  }
`;

const ProductHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-700);
`;

const ProductContent = styled.div`
  padding: 1.25rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
  }
`;

const ProductInfoGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 480px) {
    gap: 0.625rem;
    margin-bottom: 0.75rem;
  }
`;

// Define EnlargeButton first so it can be referenced in other styled components
const EnlargeButton = styled(motion.button)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
  
  &:hover ${EnlargeButton} {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const ProductName = styled.h3`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--gray-800);
  margin: 0;
  line-height: 1.3;
`;

const ProductCode = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-500);
  background: white;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  display: inline-block;
  border: 1px solid #e2e8f0;
  width: fit-content;
`;

const AttributeBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    gap: 0.3rem;
  }

  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

const AttributeBadge = styled.span`
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
  font-size: var(--text-xs);
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
  display: inline-flex;
  align-items: center;

  @media (max-width: 480px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }
`;

const ProductDetailHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: var(--rounded-lg);
  background-color: var(--gray-50);
  margin: 1.5rem 0;

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin: 0.75rem 0;
  }
`;

const ProductImage = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: var(--rounded-md);
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  margin-right: 1.5rem;
  border: 1px solid var(--gray-200);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  
  &:hover ${EnlargeButton} {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.08);
  }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductNameLarge = styled.h3`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const ProductCodeLarge = styled.div`
  font-size: var(--text-sm);
  color: var(--gray-600);
  background-color: white;
  padding: 0.25rem 0.75rem;
  border-radius: var(--rounded-full);
  display: inline-block;
  border: 1px solid var(--gray-200);
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  font-size: var(--text-sm);
  color: var(--gray-600);
  margin-bottom: 1rem;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const ActionBar = styled.div`
  border-top: 1px solid var(--gray-200);
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
`;

const ActionButton = styled(motion.button)`
  background-color: var(--primary-color);
  color: white;
  font-size: var(--text-md);
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: var(--rounded-md);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--primary-hover);
  }
  
  &:disabled {
    background-color: var(--gray-300);
    cursor: not-allowed;
  }
`;

const LightButton = styled(motion.button)`
  background-color: white;
  color: var(--gray-700);
  font-size: var(--text-md);
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--rounded-md);
  
  &:hover {
    background-color: var(--gray-100);
  }
`;

const ButtonIcon = styled.span`
  font-size: 1.2em;
`;

// Image Modal Components
const ImageModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
`;

const ImageModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
`;

const FullscreenImage = styled.img`
  width: 100%;
  height: 100%;
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  display: block;
`;

const ImageModalHeader = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  text-align: center;
`;

const ImageModalTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0 0 0.5rem;
`;

const ImageModalCode = styled.p`
  font-size: var(--text-sm);
  opacity: 0.8;
  margin: 0;
`;

const ImageModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

// Helper function to get product icon based on category
const getProductIcon = (mainCategoryId) => {
  const icons = {
    poles: '🏗️',
    decks: '🏢',
    unloading_stations: '🏭'
  };

  return icons[mainCategoryId] || '📦';
};

function ProductConfigurator() {
  const { 
    mainCategories, 
    selectedMainCategory, 
    selectedProduct,
    selectedAttributes,
    organizedProducts,
    addToQuote,
    setMainCategory,
    setSelectedProduct,
    calculateQuote
  } = useConfiguratorStore();
  
  // State for the add-to-quote animation
  const [addedToQuote, setAddedToQuote] = useState(false);
  
  // State for image modal
  const [imageModalData, setImageModalData] = useState(null);
  
  // Ref for QuantitySelector to enable scrolling
  const quantitySelectorRef = useRef(null);
  // Ref for the main scrollable content area
  const configuratorContentRef = useRef(null);
  
  // Current main category
  const currentCategory = mainCategories.find(cat => cat.id === selectedMainCategory);
  
  // Selected product object
  const productObject = selectedMainCategory && selectedProduct && organizedProducts[selectedMainCategory] 
    ? organizedProducts[selectedMainCategory].find(p => p.id === selectedProduct)
    : null;
    
    
  // Check if the current category is configurable with attributes
  const isCategoryConfigurable = () => {
    if (!selectedMainCategory) return false;

    // These categories have configurable attributes
    return ['poles', 'decks', 'unloading_stations'].includes(selectedMainCategory);
  };
  
  // Store previous product ID to prevent redundant calculations
  const prevProductRef = React.useRef(null);
  
  // Scroll to QuantitySelector function - Enhanced to scroll further down
  const scrollToQuantitySelector = React.useCallback(() => {
    if (configuratorContentRef.current) { // Target the main scrollable container
      setTimeout(() => {
        const scrollContainer = configuratorContentRef.current;
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight, // Scroll to the very bottom
          behavior: 'smooth'
        });
      }, 600); // Keep increased delay
    }
  }, []); // No dependencies, so this function is stable
  
  const handlePoleConfigurationComplete = (product) => {
    // Only calculate quote if the product changed to avoid infinite loops
    if (prevProductRef.current !== product.id) {
      prevProductRef.current = product.id;
      // The calculateQuote and scroll will be handled by the useEffect below
      // to ensure it happens after product state is fully updated.
    }
  };
  
  // Use effect to monitor product selection, update UI, and scroll
  useEffect(() => {
    if (selectedMainCategory === 'poles' && selectedProduct) {
      // Check if it's a new product selection to avoid redundant calculations/scrolls
      if (prevProductRef.current !== selectedProduct) {
        prevProductRef.current = selectedProduct;
        calculateQuote();
        scrollToQuantitySelector();
      }
    }
  }, [selectedProduct, selectedMainCategory, calculateQuote, scrollToQuantitySelector]); // Added scrollToQuantitySelector
  
  // Go back to main categories
  const handleBackToCategories = () => {
    setMainCategory(null);
  };
  
  // Go back to product list
  const handleBackToProducts = () => {
    setSelectedProduct(null);
  };
  
  // Handle adding to quote with animation feedback
  const handleAddToQuote = () => {
    // Set animation state to true
    setAddedToQuote(true);
    
    // Add to quote
    addToQuote();
    
    // Reset animation state after a delay
    setTimeout(() => {
      setAddedToQuote(false);
    }, 2000);
  };
  
  // Image modal handlers
  const handleOpenImageModal = (imageSrc, productName, productId) => {
    setImageModalData({
      imageSrc,
      productName,
      productId
    });
  };
  
  const handleCloseImageModal = () => {
    setImageModalData(null);
  };
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && imageModalData) {
        handleCloseImageModal();
      }
    };
    
    if (imageModalData) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [imageModalData]);
  
  if (!currentCategory) {
    return null;
  }
  
  // Animation for the "product flying to cart" effect
  const flyToCartAnimation = () => {
    if (!addedToQuote) return null;
    
    return (
      <motion.div
        style={{
          position: 'fixed',
          zIndex: 1000,
          bottom: '2rem',
          right: '2rem',
          width: '24px',
          height: '24px',
          backgroundColor: 'white',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
        }}
        initial={{ 
          opacity: 1, 
          scale: 0.3,
          x: -100,  // Start position relative to button
          y: 0
        }}
        animate={{ 
          opacity: [1, 1, 0],
          scale: [0.3, 1, 0.5],
          x: [0, 200, 350],  // Move to right edge of screen (quote preview)
          y: [0, -100, -200]
        }}
        transition={{ 
          duration: 1,
          times: [0, 0.7, 1],
          ease: "easeInOut" 
        }}
      >
        {selectedMainCategory === 'poles' && productObject?.attributes?.poleType ? (
          <img
            src={
              productObject.attributes.poleType === 'Lockhead Pole' ? "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles/Poles%20Section.avif" :
              productObject.attributes.poleType === 'Springhead Pole' ? "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Springhead-section.avif" :
              productObject.attributes.poleType === 'Rolling Springhead Pole' ? "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Rolling%20Springhead%20Section.avif" :
              "https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Flow-Section.avif" // Flow Pole
            }
            alt="Product"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: '14px' }}>✓</span>
        )}
      </motion.div>
    );
  };

  return (
    <ConfiguratorContainer
      $isFullWidth={selectedMainCategory === 'meet_neva'}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Flying item animation */}
      {flyToCartAnimation()}
      {!selectedMainCategory ? (
        // Welcome screen when no category is selected
        <WelcomeMessage>
          <WelcomeIcon>⚙️</WelcomeIcon>
          <WelcomeTitle>Welcome to the Indus Product Configurator</WelcomeTitle>
          <WelcomeText>
            Select a product category from the tabs above to start building your custom solution.
          </WelcomeText>
        </WelcomeMessage>
      ) : (
        // Category content when a category is selected
        <>
          <ConfiguratorContent ref={configuratorContentRef}>
            {selectedMainCategory === 'meet_neva' ? (
              // Meet Neva introduction section
              <MeetNevaSection />
            ) : selectedMainCategory === 'poles' ? (
              // Direct configuration for Poles with enhanced design
              <>
                <ConfiguratorSection>
                  <AttributeConfigurator 
                    isDirectConfiguration={true} 
                    onConfigurationComplete={handlePoleConfigurationComplete}
                  />
                </ConfiguratorSection>
                
                {selectedProduct && (
                  <ProductSummaryCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ProductHeader>
                      <ProductHeaderTitle>
                        <span>🎯</span>
                        Product Summary
                      </ProductHeaderTitle>
                    </ProductHeader>
                    
                    <ProductContent>
                      <ProductInfoGrid>
                        <ProductImageContainer>
                          {(() => {
                            const productImageSrc = getProductImageSrc(productObject, selectedMainCategory);
                            const isUrl = typeof productImageSrc === 'string' && productImageSrc.startsWith('http');
                            return isUrl ? (
                              <>
                                <LazyImage src={productImageSrc} alt={productObject.name} />
                                <EnlargeButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenImageModal(productImageSrc, productObject.name, productObject.id);
                                  }}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  ⛶
                                </EnlargeButton>
                              </>
                            ) : (
                              <span style={{ fontSize: '2rem' }}>{productImageSrc}</span>
                            );
                          })()}
                        </ProductImageContainer>
                        
                        <ProductDetails>
                          <ProductName>{productObject?.name}</ProductName>
                          <ProductCode>Item #{productObject?.id}</ProductCode>
                          
                          <AttributeBadges>
                            {productObject?.attributes?.material && (
                              <AttributeBadge>{productObject.attributes.material}</AttributeBadge>
                            )}
                            {productObject?.attributes?.size && (
                              <AttributeBadge>{productObject.attributes.size}</AttributeBadge>
                            )}
                            {productObject?.attributes?.thickness && (
                              <AttributeBadge>{productObject.attributes.thickness}</AttributeBadge>
                            )}
                          </AttributeBadges>
                        </ProductDetails>
                      </ProductInfoGrid>
                      
                      <QuantitySelector ref={quantitySelectorRef} />
                    </ProductContent>
                  </ProductSummaryCard>
                )}
              </>
            ) : selectedMainCategory === 'decks' ? (
              // New deck configurator with simplified UI
              <ConfiguratorSection>
                <DeckConfigurator />
              </ConfiguratorSection>
            ) : selectedMainCategory === 'unloading_stations' ? (
              // New unloading station configurator with consistent images
              <ConfiguratorSection>
                <UnloadingStationConfigurator />
              </ConfiguratorSection>
            ) : (
              // Standard flow for other categories
              !selectedProduct ? (
                // Product selection view
                <>
                  <ConfiguratorSection>
                    <SectionHeader>
                      <SectionTitle>
                        <SectionNumber>1</SectionNumber>
                        Select Product
                      </SectionTitle>
                    </SectionHeader>
                    
                    <ProductList />
                  </ConfiguratorSection>
                </>
              ) : (
                // Product configuration view
                <>
                  
                  <ProductDetailHeader>
                    <ProductImage>
                      {(() => {
                        const detailImageSrc = getProductImageSrc(productObject, selectedMainCategory);
                        const isDetailUrl = typeof detailImageSrc === 'string' && detailImageSrc.startsWith('http');
                        return isDetailUrl ? (
                          <>
                            <LazyImage src={detailImageSrc} alt={productObject.name} />
                            <EnlargeButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenImageModal(detailImageSrc, productObject.name, productObject.id);
                              }}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              ⛶
                            </EnlargeButton>
                          </>
                        ) : (
                          <span style={{ fontSize: '2.5rem' }}>{detailImageSrc}</span>
                        );
                      })()}
                    </ProductImage>
                    <ProductInfo>
                      <ProductNameLarge>{productObject?.name}</ProductNameLarge>
                      <ProductCodeLarge>Item #{productObject?.id}</ProductCodeLarge>
                    </ProductInfo>
                  </ProductDetailHeader>
                  
                  {/* Only show attribute configurator for configurable categories */}
                  {isCategoryConfigurable() && (
                    <ConfiguratorSection>
                      <SectionHeader>
                        <SectionTitle>
                          <SectionNumber>1</SectionNumber>
                          Configure Options
                        </SectionTitle>
                      </SectionHeader>
                      
                      <AttributeConfigurator />
                    </ConfiguratorSection>
                  )}
                  
                  <ConfiguratorSection>
                    <SectionHeader>
                      <SectionTitle>
                        <SectionNumber>{isCategoryConfigurable() ? '2' : '1'}</SectionNumber>
                        Select Quantity
                      </SectionTitle>
                    </SectionHeader>
                    
                    <QuantitySelector />
                  </ConfiguratorSection>
                </>
              )
            )}
          </ConfiguratorContent>
          
        </>
      )}
      
      {/* Image Modal */}
      <AnimatePresence>
        {imageModalData && (
          <ImageModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseImageModal}
          >
            <ImageModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ImageModalCloseButton onClick={handleCloseImageModal}>
                ×
              </ImageModalCloseButton>
              
              {typeof imageModalData.imageSrc === 'string' && imageModalData.imageSrc.startsWith('http') ? (
                <FullscreenImage 
                  src={imageModalData.imageSrc} 
                  alt={imageModalData.productName}
                />
              ) : (
                <div style={{ 
                  padding: '4rem', 
                  fontSize: '4rem', 
                  textAlign: 'center',
                  background: 'white'
                }}>
                  {imageModalData.imageSrc}
                </div>
              )}
              
              <ImageModalHeader>
                <ImageModalTitle>{imageModalData.productName}</ImageModalTitle>
                <ImageModalCode>Item #{imageModalData.productId}</ImageModalCode>
              </ImageModalHeader>
            </ImageModalContent>
          </ImageModalOverlay>
        )}
      </AnimatePresence>
    </ConfiguratorContainer>
  );
}

export default ProductConfigurator;
